import type { IubendaCookie } from '@/types'

// Extend the Window interface for type safety 
declare global {
  interface Window {
    _iub?: {
      cs: {
        api: {
          isConsentGiven: () => boolean
        }
      }
    }
  }
}

// Polls the dataLayer for the iubendaReady event and API availability. Event is pushed via GTM callback
const waitForIubendaReady = (): Promise<void> => {
  const POLL_INTERVAL_MS = 100 // How frequently to check if Iubenda is ready (in milliseconds)
  const TIMEOUT_MS = 10000 // Maximum time to wait for Iubenda to be ready (in milliseconds)
  let elapsedTime = 0

  return new Promise((resolve, reject) => {
    const interval = setInterval(() => {
      const dataLayer = window.dataLayer ?? []
      const eventReady = dataLayer.some(item => item.event === 'iubendaReady')
      
      // Iubenda api is ready, clear interval and resolve promise
      if (eventReady) {
        clearInterval(interval)
        resolve()
        return
      }

      // Timeout after 10 seconds to account for slow loading users
      elapsedTime += POLL_INTERVAL_MS
      if (elapsedTime >= TIMEOUT_MS) { 
        clearInterval(interval)
        reject(new Error('Timeout waiting for Iubenda consent API'))
      }
    }, POLL_INTERVAL_MS)
  })
}

// Retrieve cookie by value and parse as an object
const getCookie = (name: string): IubendaCookie | undefined => {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(name + '='))

  if (!match) return undefined

  const decodedValue = decodeURIComponent(match.split('=')[1])
  try {
    return JSON.parse(decodedValue)
  } catch (e) {
    return undefined
  }
}

// Return iubenda consent status
export async function iubendaIsConsentGiven (): Promise<boolean> {
  const IUBENDA_COOKIE_NAME = '_iub_cs-s3997868'

  // If GTM is disabled, return false immediately since iubenda won't be initialized
  if (import.meta.env.VITE_APP_GTM_ENABLED !== 'true') {
    return false
  }

  /**
   * Iubenda is set up to assign a cookie with "consent" or "purposes" in the object to indicate
   * the preference granularity of the user based on what compliance is required in their country.
   * "consent" and "purposes" are mutually exclusive
   * 
   * To check if a user is from a country which requires compliance, we need to check cookie['purposes']
   * To check if a user is not from a country which requires compliance, we need to check cookie['consent']
   * 
   * If an existing cookie is found from prior consent, return the value of the analytics (4) purpose in the cookie
   */
  const cookieObj = getCookie(IUBENDA_COOKIE_NAME)
  if (cookieObj) {
    return cookieObj.purposes?.['4'] ?? cookieObj.consent ?? false
  }

  return waitForIubendaReady()
    .then(() => window._iub?.cs.api.isConsentGiven() ?? false)
    .catch(() => {
      console.warn('Failed to retrieve consent from Iubenda API')
      return false
    })
}
